/** @jsxImportSource @emotion/react */
import * as React from "react";
import type { Theme } from "@emotion/react";
import { Button, H1, Input, Label, Text } from "atoms";
import { ConnectWallet, useWalletAddress } from "features/auth";
import { Head } from "features/misc";
import { GetMintPermittedResponse, getMintPermitted } from "utils/api";
import { useAsync } from "utils/hooks";
import { useMintHelpers } from "utils/mintHelpers";

const css = {
  label: {
    margin: "1rem 0",
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "baseline",
  },
  results: {
    marginTop: "1rem",
  },
  error: (theme: Theme) => ({
    color: theme.colors.primary,
    fontWeight: "bold",
    fontStyle: "italic",
    marginLeft: theme.spacing.md,
  }),
};

function MintAdmin() {
  const walletAddress = useWalletAddress();

  const { mintPermitted: adminMintPermitted, isLoading: adminLoading } =
    useMintHelpers(walletAddress);

  const { run, isLoading, isError, ...mintPermitted } =
    useAsync<GetMintPermittedResponse>();

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const checkAddress = new FormData(event.currentTarget).get(
      "walletAddress"
    ) as string;
    if (checkAddress && checkAddress.length > 0) {
      run(getMintPermitted(checkAddress));
    }
  };

  const hasData = !isLoading && mintPermitted.data !== null;
  const isApproved = hasData && mintPermitted.data?.approved;
  const isNotApproved = hasData && !mintPermitted.data?.approved;

  if (adminLoading) {
    return <Text>Loading...</Text>;
  }
  if (!walletAddress) {
    return (
      <ConnectWallet>
        <Text size="lg">Connect Your Wallet to Sign In</Text>
      </ConnectWallet>
    );
  }
  if (walletAddress && !adminMintPermitted.data?.isAdmin) {
    return <H1>Permission Denied</H1>;
  }

  return (
    <main>
      <Head title="Mint Admin" />

      <H1>Mint Wallet Check</H1>

      <form onSubmit={onSubmit}>
        <Label css={css.label}>
          Enter Wallet Address
          <Input name="walletAddress" />
        </Label>

        <div css={css.buttonWrapper}>
          <Button type="submit" disabled={isLoading} variant="gradient">
            Enter
          </Button>

          {isError && (
            <p role="alert" css={css.error}>
              Sorry, something went wrong
            </p>
          )}
        </div>
      </form>

      <div css={css.results}>
        {isApproved && (
          <p>
            <b>Approved!</b>
            <br />
            Curator:
            {mintPermitted.data?.isCurator ? "Yes" : "No"}
            <br />
            Admin:
            {mintPermitted.data?.isAdmin ? "Yes" : "No"}
            <br />
          </p>
        )}
        {isNotApproved && <b>Not Approved!</b>}
      </div>
    </main>
  );
}

export { MintAdmin };
